import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
} from "../../components/ui/dialog";
import {
  ChevronLeft,
  Check,
  Play,
  Pause,
  SkipForward,
  Timer,
  Dumbbell,
  Flame,
  ChevronDown,
  ChevronUp,
  Clock,
  Zap,
  AlertTriangle,
  History,
  Video
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import ExerciseDetailModal from "../../components/ExerciseDetailModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Technique display helper
const getTechniqueDisplay = (tech) => {
  const techniques = {
    myo_reps: { icon: "⚡", color: "#F59E0B", name: "Myo-reps" },
    dropset: { icon: "📉", color: "#EF4444", name: "Drop-set" },
    isometric: { icon: "⏱️", color: "#3B82F6", name: "Isométrico" },
    topset: { icon: "🎯", color: "#8B5CF6", name: "Top set" },
    deadstop: { icon: "⏸️", color: "#EC4899", name: "Dead-stop" },
    partials: { icon: "↕️", color: "#14B8A6", name: "Parciales" },
    negatives: { icon: "➖", color: "#F97316", name: "Negativas" },
    zigzag: { icon: "🔄", color: "#06B6D4", name: "Zig-zag" },
    rm: { icon: "🏆", color: "#D4AF37", name: "RM" },
  };
  
  const techDef = techniques[tech.type];
  if (!techDef) return null;
  
  let label = techDef.name;
  const configValue = tech.config?.value;
  
  if (configValue) {
    switch (tech.type) {
      case "dropset": label = `Drop-set (${configValue})`; break;
      case "isometric": label = `Iso ${configValue}s`; break;
      case "rm": label = `${configValue}RM`; break;
      case "myo_reps": label = `Myo (${configValue})`; break;
      default: break;
    }
  }
  
  return { ...techDef, label };
};

// Motivational messages
const completionMessages = [
  "¡Brutal! Otro día más cerca de tu mejor versión.",
  "Sin excusas, sin límites. Así se hace.",
  "El esfuerzo de hoy es el resultado de mañana.",
  "GOAT mode activado. Sigue así.",
  "Cada rep cuenta. Cada día cuenta."
];

const WorkoutMode = ({ workout, onClose, onComplete }) => {
  const { getAuthHeaders } = useAuth();
  
  // State
  const [exercises, setExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [expandedExercise, setExpandedExercise] = useState(0);
  const [seriesData, setSeriesData] = useState({});
  const [exerciseData, setExerciseData] = useState({}); // Weight/reps per exercise (not per set)
  const [isResting, setIsResting] = useState(false);
  const [restTimeLeft, setRestTimeLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [workoutStartTime] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [lastRecords, setLastRecords] = useState({});
  const [completionTriggered, setCompletionTriggered] = useState(false);
  const [techniqueModalExercise, setTechniqueModalExercise] = useState(null); // Para modal de técnica
  
  const restIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const exerciseRefs = useRef({});

  // Handle browser back button - if completion modal is showing, go to home
  useEffect(() => {
    const handlePopState = (e) => {
      if (showCompletionModal) {
        // Prevent going back to workout, go to home instead
        e.preventDefault();
        setShowCompletionModal(false);
        if (onComplete) onComplete();
      }
    };

    // Push a state so we can intercept back button
    if (showCompletionModal) {
      window.history.pushState({ completionModal: true }, '');
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showCompletionModal, onComplete]);

  // Initialize exercises
  useEffect(() => {
    if (workout?.exercises) {
      const initialSeriesData = {};
      const initialExerciseData = {};
      
      workout.exercises.forEach((ex, exIndex) => {
        const numSets = ex.sets || 3;
        initialSeriesData[exIndex] = {
          sets: Array.from({ length: numSets }, () => ({ completed: false }))
        };
        initialExerciseData[exIndex] = {
          weight: "",
          reps: "",
          useLastRecord: false
        };
      });
      
      setSeriesData(initialSeriesData);
      setExerciseData(initialExerciseData);
      setExercises(workout.exercises);
      // Ensure first exercise is expanded after data loads
      setExpandedExercise(0);
      setCurrentExerciseIndex(0);
      
      // Fetch last records
      fetchLastRecords(workout.exercises);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout]);

  // Fetch last records for exercises
  const fetchLastRecords = async (exerciseList) => {
    try {
      const headers = getAuthHeaders();
      const records = {};
      
      for (const ex of exerciseList) {
        const exerciseId = ex.exercise_id || ex.id;
        try {
          const response = await axios.get(
            `${API_URL}/api/exercise/${exerciseId}/last-record`,
            { headers }
          );
          if (response.data) {
            records[exerciseId] = response.data;
          }
        } catch (e) {
          // Expected: 404 when no previous record exists for this exercise
          // This is normal for first-time exercises, no action needed
        }
      }
      
      setLastRecords(records);
    } catch (error) {
      console.error("Error fetching last records:", error);
    }
  };

  // Rest timer logic
  useEffect(() => {
    if (isResting && !isPaused && restTimeLeft > 0) {
      restIntervalRef.current = setInterval(() => {
        setRestTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(restIntervalRef.current);
            setIsResting(false);
            if (audioRef.current) {
              audioRef.current.play().catch((err) => {
                // Audio playback may fail due to browser autoplay policies - safe to ignore
              });
            }
            toast.success("¡A por la siguiente!", { duration: 2000 });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (restIntervalRef.current) {
        clearInterval(restIntervalRef.current);
      }
    };
  }, [isResting, isPaused, restTimeLeft]);

  // Calculate progress
  const calculateProgress = useCallback(() => {
    let totalSets = 0;
    let completedSets = 0;
    
    Object.values(seriesData).forEach((ex) => {
      ex.sets.forEach((set) => {
        totalSets++;
        if (set.completed) completedSets++;
      });
    });
    
    return totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;
  }, [seriesData]);

  // Check if workout is complete
  const isWorkoutComplete = useCallback(() => {
    if (Object.keys(seriesData).length === 0) return false;
    return Object.values(seriesData).every((ex) =>
      ex.sets.every((set) => set.completed)
    );
  }, [seriesData]);

  // Get completed exercises count
  const getCompletedExercisesCount = useCallback(() => {
    return Object.values(seriesData).filter((ex) =>
      ex.sets.every((set) => set.completed)
    ).length;
  }, [seriesData]);

  // Auto-trigger completion - ONLY when truly complete
  useEffect(() => {
    // Check if all sets are complete
    const allComplete = isWorkoutComplete();
    
    if (allComplete && exercises.length > 0 && !completionTriggered && !showCompletionModal && !saving) {
      setCompletionTriggered(true);
      
      // Stop any running timer
      if (restIntervalRef.current) {
        clearInterval(restIntervalRef.current);
      }
      setIsResting(false);
      
      // Auto save and show completion after a brief delay for UX
      setTimeout(() => {
        saveAndComplete(true);
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData, exercises.length, completionTriggered, showCompletionModal, saving, isWorkoutComplete]);

  // Scroll to exercise
  const scrollToExercise = (index) => {
    const ref = exerciseRefs.current[index];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Complete a set
  const completeSet = (exerciseIndex, setIndex) => {
    const exercise = exercises[exerciseIndex];
    const restSeconds = exercise?.rest_seconds || 60;
    
    // Calculate if this is the last set of the last exercise BEFORE updating state
    const currentSets = seriesData[exerciseIndex]?.sets || [];
    const totalSetsInExercise = currentSets.length;
    const completedSetsBeforeThis = currentSets.filter((s) => s.completed).length;
    const isLastSetOfExercise = completedSetsBeforeThis === totalSetsInExercise - 1;
    const isLastExercise = exerciseIndex === exercises.length - 1;
    const isVeryLastSet = isLastExercise && isLastSetOfExercise;
    
    // Update series data
    setSeriesData((prev) => {
      const updated = { ...prev };
      updated[exerciseIndex] = {
        ...updated[exerciseIndex],
        sets: updated[exerciseIndex].sets.map((set, idx) =>
          idx === setIndex ? { ...set, completed: true } : set
        )
      };
      return updated;
    });

    // Check if this exercise is now complete
    if (isLastSetOfExercise) {
      // Exercise complete
      toast.success(`${exercise?.name || exercise?.exercise?.name} ✓`, { duration: 1500 });
      
      // If NOT the last exercise, auto-expand and scroll to next
      if (!isLastExercise) {
        setTimeout(() => {
          setCurrentExerciseIndex(exerciseIndex + 1);
          setExpandedExercise(exerciseIndex + 1);
          scrollToExercise(exerciseIndex + 1);
        }, 300);
      }
    }

    // Start rest timer ONLY if NOT the last set of the last exercise
    if (!isVeryLastSet) {
      setRestTimeLeft(restSeconds);
      setIsResting(true);
      setIsPaused(false);
    }
    // If it IS the very last set, the auto-completion useEffect will trigger
  };

  // Update exercise data (weight/reps - ONE input per exercise)
  const updateExerciseData = (exerciseIndex, field, value) => {
    setExerciseData(prev => ({
      ...prev,
      [exerciseIndex]: {
        ...prev[exerciseIndex],
        [field]: value
      }
    }));
  };

  // Apply last record for exercise
  const applyLastRecord = (exerciseIndex) => {
    const exercise = exercises[exerciseIndex];
    const exerciseId = exercise?.exercise_id || exercise?.id;
    const record = lastRecords[exerciseId];
    
    if (record) {
      setExerciseData(prev => ({
        ...prev,
        [exerciseIndex]: {
          weight: record.weight?.toString() || "",
          reps: record.reps?.toString() || "",
          useLastRecord: true
        }
      }));
      toast.success("Datos del último entrenamiento aplicados");
    }
  };

  // Skip rest
  const skipRest = () => {
    setIsResting(false);
    setRestTimeLeft(0);
    if (restIntervalRef.current) {
      clearInterval(restIntervalRef.current);
    }
  };

  // Toggle pause
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Handle exit attempt
  const handleExitAttempt = () => {
    if (isWorkoutComplete()) {
      // Already complete, just close
      onClose();
    } else {
      // Show confirmation
      setShowExitConfirm(true);
    }
  };

  // Exit without completing
  const exitWithoutCompleting = async () => {
    setSaving(true);
    try {
      // Save as incomplete - DON'T mark as completed
      const trackingData = exercises.map((ex, exIndex) => {
        const data = exerciseData[exIndex] || {};
        const setsCompleted = seriesData[exIndex]?.sets.filter(s => s.completed).length || 0;
        
        return {
          exercise_id: ex.exercise_id || ex.id,
          exercise_name: ex.name || ex.exercise?.name,
          sets: Array.from({ length: setsCompleted }, (_, i) => ({
            set_number: i + 1,
            weight: data.weight ? parseFloat(data.weight) : null,
            reps: data.reps ? parseInt(data.reps) : null,
            completed: true
          }))
        };
      });

      // Save session but mark as incomplete
      await axios.post(
        `${API_URL}/api/workout/save-incomplete-session`,
        {
          workout_day_id: workout.id,
          exercises: trackingData,
          duration_minutes: Math.round((Date.now() - workoutStartTime) / 60000),
          is_complete: false
        },
        { headers: getAuthHeaders() }
      );

      toast("Sesión guardada como incompleta", { duration: 2000 });
    } catch (error) {
      console.error("Error saving incomplete session:", error);
    } finally {
      setSaving(false);
      setShowExitConfirm(false);
      onClose();
    }
  };

  // Save and complete workout
  const saveAndComplete = async (isComplete = true) => {
    setSaving(true);
    
    try {
      const trackingData = exercises.map((ex, exIndex) => {
        const data = exerciseData[exIndex] || {};
        const setsCompleted = seriesData[exIndex]?.sets.filter(s => s.completed).length || 0;
        
        return {
          exercise_id: ex.exercise_id || ex.id,
          exercise_name: ex.name || ex.exercise?.name,
          sets: Array.from({ length: setsCompleted }, (_, i) => ({
            set_number: i + 1,
            weight: data.weight ? parseFloat(data.weight) : null,
            reps: data.reps ? parseInt(data.reps) : null,
            completed: true
          }))
        };
      });

      await axios.post(
        `${API_URL}/api/workout/complete-session`,
        {
          workout_day_id: workout.id,
          exercises: trackingData,
          duration_minutes: Math.round((Date.now() - workoutStartTime) / 60000)
        },
        { headers: getAuthHeaders() }
      );

      setShowCompletionModal(true);
    } catch (error) {
      console.error("Error saving workout:", error);
      toast.error("Error al guardar");
      setShowCompletionModal(true);
    } finally {
      setSaving(false);
    }
  };

  const progress = calculateProgress();
  const workoutDuration = Math.round((Date.now() - workoutStartTime) / 60000);
  const completedExercisesCount = getCompletedExercisesCount();
  const totalSetsCompleted = Object.values(seriesData).reduce(
    (acc, ex) => acc + ex.sets.filter(s => s.completed).length, 0
  );

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Main portal content
  const mainContent = createPortal(
    <div 
      className="fixed inset-0 bg-[#0B0B0B] z-[100] flex flex-col overflow-hidden"
      style={{ 
        paddingTop: 'max(12px, env(safe-area-inset-top))', 
        paddingBottom: 'env(safe-area-inset-bottom)' 
      }}
      data-testid="workout-mode-container"
    >
      {/* Hidden audio */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp2YjHxsXV1ld4KMlJOOhXdsX15kdH6IkJSRioF2bGJhZ3N/iY+SkYuCeG9nZGdxfIaOkpKNhX1zbWlqcXyFjo+PjIaCe3RwcHR6gIiMjYyJhYF9eXh4eXt/g4eJiYiGhIF+fHt7fH6AhIaHh4aEgn9+fX19foCChIWFhYOBf359fX5/gYKDhISEg4GAf35+fn+AgYKDg4OCgYB/fn5+f4CBgoKCgoGAf39+fn5/gIGBgoKBgYB/f35+fn+AgIGBgYGAgH9/fn5+f4CAgYGBgICAf39+fn5/gICBgYGAgIB/f35+fn9/gICBgYCAgH9/fn5+f3+AgIGBgICAf39+fn5/f4CAgICAgIB/f35+fn9/gICAgICAgH9/fn5+f39/gICAgIB/f39+fn5/f3+AgICAgH9/f35+fn9/f4CAgICAf39/fn5+f39/gICAgIB/f39+fn5/f39/gICAgH9/f35+fn9/f3+AgICAf39/fn5+f39/f4CAgIB/f39+fn5/f39/gICAgH9/f35+fn9/f3+AgICAf39/fn5+f39/f4CAgIB/f39+fn5/f39/gICAgH9/f35+fn9/f3+AgICAf39/fn5+f39/f4B/gIB/f39+fn5/f39/f4CAgH9/f35+fn9/f39/gICAf39/fn5+f39/f3+AgIB/f39+fn5/f39/f4CAgH9/f35+fn9/f39/gICAf39/fn5+f39/f3+AgIB/f39+fn5/f39/f4CAgH9/f35+fn9/f39/gICAf39/fn5+f39/f3+AgIB/f39+fn5/f39/f4CAgH9/f35+fn9/f39/gA==" />

      {/* Minimal Header */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <button
          onClick={handleExitAttempt}
          className="flex items-center gap-1 text-[#666] hover:text-white transition-colors"
          data-testid="workout-mode-exit"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">Salir</span>
        </button>
        
        <h1 className="text-white font-heading font-bold text-base truncate max-w-[180px]" data-testid="workout-mode-title">
          {workout?.name || `Día ${workout?.code}`}
        </h1>
        
        <div className="flex items-center gap-1 text-[#666] text-sm">
          <Clock className="w-4 h-4" />
          <span>{workoutDuration}m</span>
        </div>
      </div>

      {/* Progress Bar + Exercise Counter */}
      <div className="px-4 pb-3 shrink-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[#D4AF37] text-xs font-medium">
            Ejercicio {Math.min(currentExerciseIndex + 1, exercises.length)} de {exercises.length}
          </span>
          <span className="text-[#D4AF37] text-sm font-bold">{progress}%</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-[#1A1A1A]" />
      </div>

      {/* Rest Timer */}
      {isResting && (
        <div className="mx-4 mb-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl px-4 py-3 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Timer className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-[#D4AF37] font-bold text-xl">{formatTime(restTimeLeft)}</p>
                <p className="text-[#888] text-[10px]">DESCANSO</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={togglePause}
                className="h-9 w-9 rounded-full bg-[#1A1A1A] flex items-center justify-center hover:bg-[#2A2A2A]"
              >
                {isPaused ? (
                  <Play className="w-4 h-4 text-[#D4AF37]" />
                ) : (
                  <Pause className="w-4 h-4 text-[#D4AF37]" />
                )}
              </button>
              <button
                onClick={skipRest}
                className="h-9 w-9 rounded-full bg-[#1A1A1A] flex items-center justify-center hover:bg-[#2A2A2A]"
              >
                <SkipForward className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exercise List */}
      <div className="flex-1 overflow-y-auto px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="space-y-2.5 pb-28">
          {exercises.map((exercise, exIndex) => {
            const exData = seriesData[exIndex];
            const completedSets = exData?.sets.filter((s) => s.completed).length || 0;
            const totalSets = exData?.sets.length || exercise.sets || 3;
            const isComplete = completedSets === totalSets;
            const isExpanded = expandedExercise === exIndex;
            const exerciseName = exercise.name || exercise.exercise?.name;
            const exerciseId = exercise.exercise_id || exercise.id;
            const hasLastRecord = lastRecords[exerciseId];
            const currentData = exerciseData[exIndex] || {};

            return (
              <Card
                key={exIndex}
                ref={(el) => exerciseRefs.current[exIndex] = el}
                className={cn(
                  "bg-[#121212] border transition-all overflow-hidden",
                  isComplete
                    ? "border-[#22C55E]/40"
                    : exIndex === currentExerciseIndex
                    ? "border-[#D4AF37]/50"
                    : "border-[#1A1A1A]"
                )}
              >
                {/* Exercise Header */}
                <button
                  className="w-full p-3 flex items-center gap-3"
                  onClick={() => setExpandedExercise(isExpanded ? -1 : exIndex)}
                >
                  <div
                    className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 font-bold",
                      isComplete
                        ? "bg-[#22C55E] text-white"
                        : "bg-[#1A1A1A] text-[#D4AF37]"
                    )}
                  >
                    {isComplete ? <Check className="w-5 h-5" /> : exIndex + 1}
                  </div>
                  
                  <div className="flex-1 text-left min-w-0">
                    <h3 className={cn(
                      "font-medium truncate text-sm",
                      isComplete ? "text-[#22C55E]" : "text-white"
                    )}>
                      {exerciseName}
                    </h3>
                    <div className="flex items-center gap-2 text-[11px] text-[#666]">
                      <span>{totalSets}×{exercise.reps}</span>
                      {exercise.rir && <span>RIR {exercise.rir}</span>}
                      <span className={cn(
                        "font-semibold",
                        isComplete ? "text-[#22C55E]" : "text-[#D4AF37]"
                      )}>
                        {completedSets}/{totalSets}
                      </span>
                    </div>
                  </div>
                  
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-[#555] shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#555] shrink-0" />
                  )}
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-[#1A1A1A] pt-3">
                    {/* Techniques */}
                    {exercise.techniques?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {exercise.techniques.map((tech, idx) => {
                          const display = getTechniqueDisplay(tech);
                          if (!display) return null;
                          return (
                            <span
                              key={idx}
                              className="text-[10px] px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: `${display.color}15`,
                                color: display.color
                              }}
                            >
                              {display.icon} {display.label}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Ver Técnica Button - Opens Exercise Detail Modal */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTechniqueModalExercise({
                          ...exercise,
                          ...(exercise.exercise || {}),
                          sets: exercise.sets,
                          reps: exercise.reps,
                          rest_seconds: exercise.rest_seconds || 60
                        });
                      }}
                      className="w-full mb-3 flex items-center justify-center gap-2 py-2.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg text-[#D4AF37] text-sm font-medium hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`technique-btn-${exIndex}`}
                    >
                      <Video className="w-4 h-4" />
                      Ver técnica y vídeo
                    </button>

                    {/* Weight/Reps Input - ONE per exercise */}
                    {!isComplete && (
                      <div className="mb-3 p-2.5 bg-[#0B0B0B] rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Input
                            type="number"
                            placeholder="Peso (kg)"
                            value={currentData.weight || ""}
                            onChange={(e) => updateExerciseData(exIndex, "weight", e.target.value)}
                            className="h-9 bg-[#1A1A1A] border-[#2A2A2A] text-white text-center text-sm flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="Reps"
                            value={currentData.reps || ""}
                            onChange={(e) => updateExerciseData(exIndex, "reps", e.target.value)}
                            className="h-9 bg-[#1A1A1A] border-[#2A2A2A] text-white text-center text-sm flex-1"
                          />
                        </div>
                        {hasLastRecord && !currentData.useLastRecord && (
                          <button
                            onClick={() => applyLastRecord(exIndex)}
                            className="w-full flex items-center justify-center gap-1.5 text-[#D4AF37] text-xs py-1.5 hover:bg-[#1A1A1A] rounded transition-colors"
                          >
                            <History className="w-3.5 h-3.5" />
                            Usar último: {hasLastRecord.weight}kg × {hasLastRecord.reps}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Series Grid */}
                    <div className="grid grid-cols-4 gap-2">
                      {exData?.sets.map((set, setIndex) => (
                        <button
                          key={setIndex}
                          onClick={() => !set.completed && completeSet(exIndex, setIndex)}
                          disabled={set.completed}
                          className={cn(
                            "aspect-square rounded-xl flex flex-col items-center justify-center transition-all",
                            set.completed
                              ? "bg-[#22C55E] text-white scale-95"
                              : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A] hover:text-white active:scale-95"
                          )}
                        >
                          {set.completed ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <>
                              <span className="text-base font-bold">{setIndex + 1}</span>
                              <span className="text-[9px] -mt-0.5">serie</span>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                    
                    <p className="text-center text-[#444] text-[10px] mt-2">
                      Descanso: {exercise.rest_seconds || 60}s
                    </p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Bottom Status */}
      <div 
        className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-6 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/95 to-transparent"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <div className="bg-[#121212] rounded-xl p-3 border border-[#1A1A1A] flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-sm">
              {completedExercisesCount}/{exercises.length} ejercicios
            </p>
            <p className="text-[#555] text-xs">{totalSetsCompleted} series</p>
          </div>
          <div className="text-right">
            <p className="text-[#D4AF37] font-bold">{progress}%</p>
            <p className="text-[#555] text-xs">{workoutDuration}min</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

  // Render the main portal content along with dialogs (which have their own portals)
  return (
    <>
      {mainContent}
      
      {/* Exit Confirmation Modal - rendered outside main portal for proper z-index */}
      <Dialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-xs mx-4 z-[200]">
          <div className="text-center py-4">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-[#F59E0B]" />
            </div>
            
            <h3 className="text-white font-heading font-bold text-lg mb-2">
              ¿Quieres salir del entrenamiento?
            </h3>
            
            <p className="text-[#888] text-sm mb-6">
              Tu progreso se guardará como sesión incompleta. No contará como día completado.
            </p>

            <div className="space-y-2">
              <Button
                onClick={() => setShowExitConfirm(false)}
                className="w-full py-3 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold rounded-xl"
                data-testid="exit-modal-continue"
              >
                Continuar entrenando
              </Button>
              
              <Button
                onClick={exitWithoutCompleting}
                disabled={saving}
                variant="ghost"
                className="w-full text-[#888] hover:text-white hover:bg-[#1A1A1A]"
                data-testid="exit-modal-leave"
              >
                {saving ? "Guardando..." : "Salir"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion Modal - rendered outside main portal for proper z-index */}
      <Dialog open={showCompletionModal} onOpenChange={() => {}}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-sm mx-4 [&>button]:hidden z-[200]">
          <div className="text-center py-6">
            {/* Fire Animation */}
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-orange-500/30 to-red-500/20 flex items-center justify-center animate-pulse">
              <Flame className="w-10 h-10 text-orange-500" />
            </div>
            
            <h2 className="text-2xl font-heading font-black text-white mb-2">
              Sesión Completada
            </h2>
            
            <p className="text-[#888] text-sm mb-6 px-4">
              {completionMessages[Math.floor(Math.random() * completionMessages.length)]}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6 px-2">
              <div className="bg-[#1A1A1A] rounded-xl p-3">
                <Dumbbell className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
                <p className="text-white font-bold">{completedExercisesCount}</p>
                <p className="text-[#555] text-[10px]">Ejercicios</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-xl p-3">
                <Clock className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
                <p className="text-white font-bold">{workoutDuration}</p>
                <p className="text-[#555] text-[10px]">Minutos</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-xl p-3">
                <Zap className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
                <p className="text-white font-bold">{totalSetsCompleted}</p>
                <p className="text-[#555] text-[10px]">Series</p>
              </div>
            </div>

            <Button
              onClick={() => {
                setShowCompletionModal(false);
                if (onComplete) onComplete();
              }}
              className="w-full py-4 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold rounded-xl"
              data-testid="workout-complete-back-home"
            >
              VOLVER AL INICIO
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Technique Modal - Shows exercise details, video, tips */}
      {techniqueModalExercise && (
        <ExerciseDetailModal
          exercise={techniqueModalExercise}
          onClose={() => setTechniqueModalExercise(null)}
          onStartTracking={() => {
            // Return to workout mode at the same exercise
            setTechniqueModalExercise(null);
          }}
          onStartTimer={(seconds) => {
            setTechniqueModalExercise(null);
            setRestTimeLeft(seconds);
            setIsResting(true);
            setIsPaused(false);
          }}
        />
      )}
    </>
  );
};

export default WorkoutMode;
